/**
 * swiper/react 懒加载器 / webpack alias 入口
 *
 * 双重用途：
 * 1. webpack alias：webpack.config.js 中 'swiper/react$' → 本文件
 *    所有业务代码的 `import { Swiper, SwiperSlide } from 'swiper/react'` 自动走懒加载，无需修改
 * 2. global.js 挂载：导出 GlobalSwiper / GlobalSwiperSlide 供外部模块通过 global.Swiper 访问
 *
 * 内部使用 'swiper/react-original'（alias 到真实 node_modules）做动态 import，
 * 绕过外层 alias 避免循环引用（与 mobx-react / mobx-react-original 模式完全一致）。
 */
import React from 'react';

// 模块级缓存：加载成功后的 swiper/react 模块（含 Swiper / SwiperSlide 等导出）
let swiperReactModule = null;
// 模块级缓存：正在进行中的加载 Promise，保证多处并发调用只发一次网络请求
let swiperReactPromise = null;

/**
 * 懒加载 swiper/react 及其样式（幂等）。
 * - 已加载：直接返回已缓存模块
 * - 加载中：复用同一个 Promise
 * - 未加载：发起动态 import，同时把 JS 与 CSS 打进同一个 "swiper" chunk
 *   （CSS 必须一起加，否则仅通过 global.Swiper 消费的外部模块会缺样式）
 */
export const loadSwiperReact = () => {
  if (swiperReactModule) {
    return Promise.resolve(swiperReactModule);
  }

  if (!swiperReactPromise) {
    swiperReactPromise = Promise.all([
      import(/* webpackChunkName: "swiper" */ 'swiper/react-original'), // 用 -original 绕过自身 alias 避免循环
      import(/* webpackChunkName: "swiper" */ 'swiper/swiper.less')
    ])
      .then(([module]) => {
        swiperReactModule = module;
        return module;
      })
      .catch((error) => {
        // 失败时重置 Promise，允许后续重试
        swiperReactPromise = null;
        console.error('Failed to load swiper/react chunk:', error);
        throw error;
      });
  }

  return swiperReactPromise;
};

// 从已加载模块中取出目标组件（Swiper 或 SwiperSlide）；未加载时返回 null
const getComponent = (picker) => (swiperReactModule ? picker(swiperReactModule) : null);

/**
 * 生成一个「懒加载包装组件」：
 * - 未加载时渲染 fallback（默认 null），并在 effect 里触发懒加载
 * - 加载完成后 setState 切换为真正的 swiper 组件，并透传 ref 与 props
 *
 * @param {(module) => React.Component} picker 从模块中选取具体组件
 * @param {string} displayName 便于 devtools 调试的名称
 */
const createLazySwiperComponent = (picker, displayName) => {
  const LazySwiperComponent = React.forwardRef(({ fallback = null, ...props }, ref) => {
    // 初始值：若模块已在其他地方加载过，直接拿到组件，避免一次多余渲染
    const [InnerComponent, setInnerComponent] = React.useState(() => getComponent(picker));

    React.useEffect(() => {
      // 已经拿到组件就不需要再加载
      if (InnerComponent) {
        return undefined;
      }

      // mounted 标记：防止组件卸载后异步回调里还 setState
      let mounted = true;

      // 每个包装组件都自己 await 同一个缓存 Promise（loadSwiperReact 幂等），
      // 加载完成后各自同步一次即可
      loadSwiperReact()
        .then(() => {
          if (mounted) {
            setInnerComponent(() => getComponent(picker));
          }
        })
        .catch(() => {});

      return () => {
        mounted = false;
      };
    }, [InnerComponent]);

    // 加载完成前渲染 fallback（可传入骨架/占位，避免布局抖动）
    if (!InnerComponent) {
      return fallback;
    }

    return <InnerComponent ref={ref} {...props} />;
  });

  LazySwiperComponent.displayName = displayName;

  return LazySwiperComponent;
};

// ---- webpack alias 命名导出（所有 import { X } from 'swiper/react' 最终走这里） ----
export const Swiper = createLazySwiperComponent((module) => module.Swiper, 'Swiper');
export const SwiperSlide = createLazySwiperComponent((module) => module.SwiperSlide, 'SwiperSlide');

/**
 * useSwiperSlide hook 的懒加载代理。
 * 该 hook 只会在 <SwiperSlide> 的子组件内被调用，
 * 而 <SwiperSlide> 本身是懒加载包装组件：只有 swiperReactModule 加载完成后才渲染子节点，
 * 因此 useSwiperSlide 被调用时 swiperReactModule 必然已加载，不存在条件调用问题。
 */
export function useSwiperSlide() {
  return swiperReactModule ? swiperReactModule.useSwiperSlide() : {};
}



// WEBPACK FOOTER //
// ./src/utils/lazyLoad/swiperReactLoader.js